
import React, { useMemo } from 'react'
import {
  BaseEdge,
  getBezierPath,
  type EdgeProps,
  useStore,
  useNodesData,
} from '@xyflow/react'
/**
 * GenieEdge - Custom React Flow edge
 *
 * Uses React Flow's built-in getBezierPath for reliable edge rendering,
 * with custom styling: gradient colors based on node types,
 * variants, zoom-aware labels, and wide hitbox.
 */

export type GenieEdgeVariant = 'default' | 'animated' | 'dashed' | 'thick' | 'floating'

export interface GenieEdgeData extends Record<string, unknown> {
  variant?: GenieEdgeVariant
  label?: string
}

/* ---- Constants ---- */
const STROKE = {
  BASE: 1.5,
  ACTIVE: 2,
  THICK: 4,
  HITBOX: 20,
}

const COLORS = {
  DEFAULT_LIGHT: '#999',
  DEFAULT_DARK: '#777',
  SELECTED: '#1fb6ff',
  HOVER: '#3b82f6',
  RECONNECTING: '#94a3b8',
}

/* ---- Variant styles ---- */
function getVariantStyle(variant: GenieEdgeVariant) {
  switch (variant) {
    case 'animated':
      return {
        strokeDasharray: '5,5',
        animation: 'edgeFlow 0.5s linear infinite',
        willChange: 'stroke-dashoffset',
      }
    case 'dashed':
      return { strokeDasharray: '8,4' }
    case 'thick':
      return { strokeWidth: STROKE.THICK }
    case 'floating':
      return { opacity: 0.7, strokeDasharray: '2,2' }
    default:
      return {}
  }
}

/** Edge accent colors per node type — matches scaffold palette */
const NODE_EDGE_COLORS: Record<string, string> = {
  text:       '#2979FF',  // blue
  image:      '#7C4DFF',  // purple
  video:      '#F44336',  // red
  '3d':       '#00BCD4',  // teal
  shader:     '#E91E63',  // pink
  plugin:     '#FFD600',  // yellow
}
const DEFAULT_EDGE_COLOR = '#4CAF50' // green (text-file, image-file, etc.)

/** Resolve edge color from node type */
function getNodeEdgeColor(nodeData: Record<string, unknown> | undefined): string {
  if (!nodeData) return COLORS.DEFAULT_LIGHT
  const nodeType = (nodeData.nodeType || nodeData.config && (nodeData.config as Record<string, unknown>).type) as string | undefined
  if (!nodeType) return COLORS.DEFAULT_LIGHT
  return NODE_EDGE_COLORS[nodeType] || DEFAULT_EDGE_COLOR
}

function GenieEdgeComponent({
  id,
  source,
  target,
  sourceHandleId,
  targetHandleId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  style,
  markerEnd,
  markerStart,
}: EdgeProps) {
  const edgeData = (data || {}) as GenieEdgeData
  const variant = edgeData.variant || 'default'
  const label = edgeData.label

  // Quantized zoom tier — avoids re-render on every frame during zoom.
  // Tier 0: z < 0.5, Tier 1: 0.5 <= z < 1.0, Tier 2: z >= 1.0
  const zoomTier = useStore((s) => {
    const z = s.transform[2]
    if (z >= 1.0) return 2
    if (z >= 0.5) return 1
    return 0
  })

  // Look up source and target node data for handle colors
  const nodesData = useNodesData([source, target])
  const sourceNodeData = nodesData[0]?.data as Record<string, unknown> | undefined
  const targetNodeData = nodesData[1]?.data as Record<string, unknown> | undefined

  const sourceColor = getNodeEdgeColor(sourceNodeData)
  const targetColor = getNodeEdgeColor(targetNodeData)

  const gradientId = `edge-gradient-${id}`
  const useGradient = !selected && sourceColor !== targetColor

  // Use React Flow's built-in bezier path computation
  // This properly accounts for sourcePosition/targetPosition (handle sides)
  const [path, labelX, labelY] = useMemo(
    () => getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    }),
    [sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition],
  )

  const strokeColor = selected
    ? COLORS.SELECTED
    : useGradient
      ? `url(#${gradientId})`
      : sourceColor
  const strokeWidth = selected ? STROKE.ACTIVE : STROKE.BASE
  const variantStyle = getVariantStyle(variant)

  return (
    <>
      {/* SVG gradient definition */}
      {useGradient && (
        <defs>
          <linearGradient
            id={gradientId}
            x1={sourceX}
            y1={sourceY}
            x2={targetX}
            y2={targetY}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={sourceColor} />
            <stop offset="100%" stopColor={targetColor} />
          </linearGradient>
        </defs>
      )}

      <BaseEdge
        id={id}
        path={path}
        interactionWidth={STROKE.HITBOX}
        markerEnd={markerEnd}
        markerStart={markerStart}
        className="genie-edge"
        style={{
          stroke: strokeColor,
          strokeWidth,
          transition: 'stroke 0.15s, stroke-width 0.15s',
          ...variantStyle,
          ...style,
        }}
      />

      {/* Zoom-aware label */}
      {label && zoomTier >= 1 && (
        <foreignObject
          x={labelX - 50}
          y={labelY - 12}
          width={100}
          height={24}
          style={{ overflow: 'visible', pointerEvents: 'none' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 11,
              color: 'var(--foreground)',
              whiteSpace: 'nowrap',
              ...(zoomTier >= 2
                ? {
                    background: 'var(--background)',
                    padding: '2px 6px',
                    borderRadius: 4,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }
                : {}),
            }}
          >
            {label}
          </div>
        </foreignObject>
      )}
    </>
  )
}

export const GenieEdge = React.memo(GenieEdgeComponent)
GenieEdge.displayName = 'GenieEdge'
